import React, { useEffect, useState } from 'react';
import './DirectionalIndicators.scss';

const DirectionalIndicators = ({ units, map, mapContainer, onUnitSelect, selectedUnit, onRecenterUnit }) => {
  const [offScreenUnits, setOffScreenUnits] = useState([]);
  const [hoveredUnit, setHoveredUnit] = useState(null);

  const adjustPositionsToAvoidOverlap = (indicators, mapWidth, mapHeight) => {
    const labelWidth = 40; // Approximate label width
    const labelHeight = 24; // Approximate label height
    const minSpacing = 5; // Minimum spacing between labels
    
    // Group indicators by edge
    const edges = { top: [], bottom: [], left: [], right: [] };
    indicators.forEach(indicator => {
      edges[indicator.edge].push(indicator);
    });
    
    // Adjust positions for each edge
    Object.keys(edges).forEach(edge => {
      const edgeIndicators = edges[edge];
      if (edgeIndicators.length <= 1) return;
      
      // Sort by position along the edge
      if (edge === 'top' || edge === 'bottom') {
        edgeIndicators.sort((a, b) => a.x - b.x);
      } else {
        edgeIndicators.sort((a, b) => a.y - b.y);
      }
      
      // Distribute evenly if overlapping
      for (let i = 1; i < edgeIndicators.length; i++) {
        const current = edgeIndicators[i];
        const previous = edgeIndicators[i - 1];
        
        if (edge === 'top' || edge === 'bottom') {
          const minDistance = labelWidth + minSpacing;
          if (current.x - previous.x < minDistance) {
            current.x = previous.x + minDistance;
            // Keep within bounds
            if (current.x > mapWidth - 20) {
              // Redistribute all labels evenly
              const availableWidth = mapWidth - 40; // 20px margin on each side
              const spacing = availableWidth / (edgeIndicators.length + 1);
              edgeIndicators.forEach((indicator, idx) => {
                indicator.x = 20 + spacing * (idx + 1);
              });
              break;
            }
          }
        } else {
          const minDistance = labelHeight + minSpacing;
          if (current.y - previous.y < minDistance) {
            current.y = previous.y + minDistance;
            // Keep within bounds
            if (current.y > mapHeight - 20) {
              // Redistribute all labels evenly
              const availableHeight = mapHeight - 40; // 20px margin on each side
              const spacing = availableHeight / (edgeIndicators.length + 1);
              edgeIndicators.forEach((indicator, idx) => {
                indicator.y = 20 + spacing * (idx + 1);
              });
              break;
            }
          }
        }
      }
    });
    
    return indicators;
  };

  const calculateOffScreenUnits = () => {
    if (!map || !mapContainer || !units.length) {
      setOffScreenUnits([]);
      return;
    }

    const bounds = map.getBounds();
    if (!bounds) return;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    
    const mapRect = map.getDiv().getBoundingClientRect();
    const indicators = [];

    units
      .filter(unit => unit.last_pos_latitude && unit.last_pos_longitude)
      .forEach(unit => {
        const lat = unit.last_pos_latitude;
        const lng = unit.last_pos_longitude;
        
        // Check if unit is outside current map bounds
        const isOutside = lat > ne.lat() || lat < sw.lat() || lng > ne.lng() || lng < sw.lng();
        
        if (isOutside) {
          // Calculate direction and position for indicator
          const centerLat = (ne.lat() + sw.lat()) / 2;
          const centerLng = (ne.lng() + sw.lng()) / 2;
          
          // Position indicator on appropriate map edge based on unit location
          const baseMargin = 20;
          const mapWidth = mapRect.width;
          const mapHeight = mapRect.height;
          
          let x, y, edge;
          
          // Determine which edge and position along that edge
          if (lng > ne.lng()) {
            // Right edge
            edge = 'right';
            x = mapWidth - baseMargin;
            // Position along right edge based on latitude, clamped to visible area
            const latPercent = Math.max(0.1, Math.min(0.9, (lat - sw.lat()) / (ne.lat() - sw.lat())));
            y = mapHeight - (latPercent * mapHeight);
          } else if (lng < sw.lng()) {
            // Left edge
            edge = 'left';
            x = baseMargin;
            const latPercent = Math.max(0.1, Math.min(0.9, (lat - sw.lat()) / (ne.lat() - sw.lat())));
            y = mapHeight - (latPercent * mapHeight);
          } else if (lat > ne.lat()) {
            // Top edge
            edge = 'top';
            y = baseMargin;
            const lngPercent = Math.max(0.1, Math.min(0.9, (lng - sw.lng()) / (ne.lng() - sw.lng())));
            x = lngPercent * mapWidth;
          } else {
            // Bottom edge
            edge = 'bottom';
            y = mapHeight - baseMargin;
            const lngPercent = Math.max(0.1, Math.min(0.9, (lng - sw.lng()) / (ne.lng() - sw.lng())));
            x = lngPercent * mapWidth;
          }
          
          const unitIdentifier = unit.unit_id;
          
          indicators.push({
            unitId: unit.unit_id,
            unitIdentifier,
            unitName: unit.name,
            x,
            y,
            edge
          });
        }
      });

    // Prevent overlapping by adjusting positions
    const adjustedIndicators = adjustPositionsToAvoidOverlap(indicators, mapRect.width, mapRect.height);
    setOffScreenUnits(adjustedIndicators);
  };

  useEffect(() => {
    if (!map) return;
    
    calculateOffScreenUnits();
    
    const listeners = [
      map.addListener('bounds_changed', calculateOffScreenUnits),
      map.addListener('zoom_changed', calculateOffScreenUnits)
    ];
    
    return () => listeners.forEach(listener => listener.remove());
  }, [map, units]);

  return (
    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 50 }}>
      {offScreenUnits.map(indicator => (
        <div
          key={indicator.unitId}
          className={`directional-indicator ${selectedUnit === indicator.unitId ? 'selected' : ''}`}
          style={{
            position: 'absolute',
            left: indicator.x,
            top: indicator.y,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'auto'
          }}
          onMouseEnter={() => setHoveredUnit(indicator.unitId)}
          onMouseLeave={() => setHoveredUnit(null)}
          onClick={() => {
            if (selectedUnit === indicator.unitId && onRecenterUnit) {
              onRecenterUnit();
            } else if (onUnitSelect) {
              onUnitSelect(indicator.unitId);
            }
          }}
        >
          {indicator.unitIdentifier}
          {hoveredUnit === indicator.unitId && (
            <div className={`directional-tooltip tooltip-${indicator.edge}`}>
              {indicator.unitName}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default DirectionalIndicators;